import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { CustomWindow } from '@/main';
import { logger } from '@/services/logger';

declare let window: CustomWindow;
let DB = window.__IDB;

const SCHEMA_VERSION = 8;
const DATABASE_NAME = 'bManagedCacheStore';
const STORES = {
    optionLists: {
        objectStoreName: 'optionLists',
        keys: ['name', 'dataModelHash'],
        indexes: {
            optionListName: 'name'
        }
    },
    translations: {
        objectStoreName: 'translations',
        keys: ['languageKey'],
        indexes: {
            languageKey: 'languageKey'
        }
    },
    additionalCategories: {
        objectStoreName: 'additionalCategories',
        keys: ['additionalModule', 'additionalCategoryNameOrCode', 'dataModelHash', 'translatedLanguage'],
        indexes: {
            additionalCategoryNameOrCode: 'additionalCategoryNameOrCode'
        }
    },
    contacts: {
        objectStoreName: 'contacts',
        keys: ['userId', 'organizationId'],
        indexes: {
            userId: 'userId'
        }
    },
    communicationChannels: {
        objectStoreName: 'communicationChannels',
        keys: ['userId', 'organizationId'],
        indexes: {
            userId: 'userId'
        }
    }
};

interface IndexedDBApi<R> {
    add: (key: IDBValidKey, record: R) => Promise<void>;
    delete: (key: IDBValidKey) => Promise<void>;
    deleteAllByIndex: (indexName: string, key: IDBValidKey) => Promise<void>;
    deleteAll: () => Promise<void>;
    get: (key: IDBValidKey) => Promise<R | undefined>;
}

const useIndexedDB = <R,>(storeName: string): IndexedDBApi<R> => {

    const isDbInitialized = useCallback(() => {
        if (DB) {
            return true;
        }

        return false;
    }, []);

    const getTransaction = useCallback(() => {

        if (!isDbInitialized()) {
            throw new Error('useIndexedDB: Premature call to "getTransaction" while the DB is not initialized');
        }

        const transaction = DB.transaction([storeName], 'readwrite');
        return transaction.objectStore(storeName);
    }, [isDbInitialized, storeName]);

    const add = useCallback(
        (key: IDBValidKey, record: R) =>
            new Promise<void>((resolve) => {
                try {
                    if (!isDbInitialized()) {
                        logger.warn('useIndexedDB: Premature call to "add" while the Indexed DB is not initialized');
                        return resolve();
                    }

                    const transaction = getTransaction();
                    const request = transaction.put(record);

                    request.onsuccess = () => {
                        logger.debug(`useIndexedDB: Successfully stored record with key ${key}`);
                        resolve();
                    };

                    request.onerror = (event) => {
                        const errName = request.error?.name;
                        if (errName === 'QuotaExceededError') {
                            logger.error(
                                `useIndexedDB: QuotaExceededError while storing key ${key} in store "${storeName}". `
                                + 'Consider clearing browser storage or reducing the cached dataset size.',
                                event,
                            );
                        } else {
                            logger.warn(`useIndexedDB: Could not add record with key ${key}`, event);
                        }
                        resolve();
                    };
                } catch (exp) {
                    if (exp instanceof DOMException && exp.name === 'QuotaExceededError') {
                        logger.error(
                            `useIndexedDB: QuotaExceededError while storing key ${key} in store "${storeName}".`,
                            exp,
                        );
                    } else {
                        logger.warn(`useIndexedDB: Could not add record with key ${key}`, exp);
                    }
                    resolve();
                }
            }),
        [getTransaction, isDbInitialized, storeName]
    );

    const del = useCallback(
        (key: IDBValidKey) =>
            new Promise<void>((resolve) => {
                try {
                    if (!isDbInitialized()) {
                        logger.warn('useIndexedDB: Premature call to "delete" while the Indexed DB is not initialized');
                        return resolve();
                    }

                    const transaction = getTransaction();
                    const transactionKey = typeof key === 'string' ? [key] : key;
                    const request = transaction.delete(transactionKey);

                    request.onsuccess = () => {
                        logger.debug(`useIndexedDB: Successfully deleted record with key ${key}`);
                        resolve();
                    };

                    request.onerror = (event) => {
                        logger.warn(`useIndexedDB: Could not delete record with key ${key}`, event);
                        resolve();
                    };
                } catch (exp) {
                    logger.warn(`useIndexedDB: Could not delete record with key ${key}`, exp);
                    resolve();
                }
            }),
        [getTransaction, isDbInitialized]
    );

    const getRecord = useCallback(
        (key: IDBValidKey) =>
            new Promise<R | undefined>((resolve) => {
                try {
                    if (!isDbInitialized()) {
                        logger.warn('useIndexedDB: Premature call to "get" while the Indexed DB is not initialized');
                        return resolve(undefined);
                    }

                    const transaction = getTransaction();
                    const transactionKey = typeof key === 'string' ? [key] : key;
                    const request = transaction.get(transactionKey);

                    request.onsuccess = () => {
                        if (request.result) {
                            logger.debug(`useIndexedDB: Cache hit for key ${key}`);
                        } else {
                            logger.debug(`useIndexedDB: Cache miss for key ${key}`);
                        }
                        resolve(request.result);
                    };

                    request.onerror = (event) => {
                        logger.warn(`useIndexedDB: Could not get record with key ${key}`, event);
                        resolve(undefined);
                    };
                } catch (exp) {
                    logger.warn(`useIndexedDB: Could not get record with key ${key}`, exp);
                    resolve(undefined);
                }
            }),
        [getTransaction, isDbInitialized]
    );

    const deleteAllByIndex = useCallback(
        (indexName: string, key: IDBValidKey) =>
            new Promise<void>((resolve) => {
                try {
                    if (!isDbInitialized()) {
                        logger.warn(
                            'useIndexedDB: Premature call to "deleteAllByIndex" while the Indexed DB is not initialized'
                        );
                        return resolve();
                    }

                    const transaction = getTransaction();
                    const index = transaction.index(indexName);
                    const cursorRequest = index.openKeyCursor(IDBKeyRange.only(key));

                    cursorRequest.onsuccess = () => {
                        const cursor = cursorRequest.result;
                        if (cursor) {
                            transaction.delete(cursor.primaryKey);
                            cursor.continue();
                        } else {
                            logger.debug(
                                `useIndexedDB: Successfully deleted records with key ${key} and index ${indexName}`
                            );
                            resolve();
                        }
                    };

                    cursorRequest.onerror = (event) => {
                        logger.warn(`useIndexedDB: Could not delete records with key ${key}`, event);
                        resolve();
                    };
                } catch (exp) {
                    logger.warn(
                        `useIndexedDB: Could not delete records with key ${key} and index '${indexName}'`,
                        exp
                    );
                    resolve();
                }
            }),
        [getTransaction, isDbInitialized]
    );

    const deleteAll = useCallback(
        () =>
            new Promise<void>((resolve) => {
                try {
                    if (!isDbInitialized()) {
                        logger.warn(
                            'useIndexedDB: Premature call to "deleteAllByIndex" while the Indexed DB is not initialized'
                        );
                        return resolve();
                    }

                    const transaction = getTransaction();
                    const cursorRequest = transaction.openKeyCursor();

                    cursorRequest.onsuccess = () => {
                        const cursor = cursorRequest.result;
                        if (cursor) {
                            transaction.delete(cursor.primaryKey);
                            cursor.continue();
                        } else {
                            logger.debug(`useIndexedDB: Successfully deleted records in store ${storeName}`);
                            resolve();
                        }
                    };

                    cursorRequest.onerror = (event) => {
                        logger.warn(`useIndexedDB: Could not delete all records in store ${storeName}`, event);
                        resolve();
                    };
                } catch (exp) {
                    logger.warn(`useIndexedDB: Could not delete all records in store ${storeName}`, exp);
                    resolve();
                }
            }),
        [getTransaction, isDbInitialized, storeName]
    );

    return useMemo(
        () => ({
            add,
            delete: del,
            deleteAllByIndex,
            deleteAll,
            get: getRecord,
        }),
        [add, del, deleteAll, deleteAllByIndex, getRecord]
    );
};

/**
 * The provider only initializes the IndexedDB shared object with the AngularJS app, it
 * doesn't actually provide any context for the hook.
 */
const IndexedDBProvider = ({ children }: React.PropsWithChildren) => {

    const [loading, setLoading] = useState(true);

    useEffect(() => {

        if (window.__IDB) {
            // Indexed DB is already initialized via AngularJS
            DB = window.__IDB;
            setLoading(false);

        } else {

            // This case will only be executed in case of:
            // 1- that we have totally migrated the AngularJS app.
            // 2- micro-frontends
            // 3- storybook
            //
            // as the `window.__IDB` will be already initialized in the AngularJS app.

            const DBOpenRequest = window.indexedDB.open(DATABASE_NAME, SCHEMA_VERSION);

            DBOpenRequest.onsuccess = function() {
                logger.debug('IndexedDBProvider: Created IndexedDB database.');

                DB = DBOpenRequest.result;

                setLoading(false);
            };

            DBOpenRequest.onupgradeneeded = function() {

                // We can only create object stores in this step, so change the SCHEMA_VERSION every time you change
                // this function (adding new stores or indexes), otherwise `onupgradeneeded` won't be invoked.
                // Make sure to drop any object stores that were introduced in previous versions.
                DB = DBOpenRequest.result;

                DB.onerror = function(event) {
                    logger.error('IndexedDBProvider: Error loading IndexedDB database.', event);
                };

                const optionListsNeedMigration = DB.objectStoreNames.contains(STORES.optionLists.objectStoreName);
                const translationsNeedMigration = DB.objectStoreNames.contains(STORES.translations.objectStoreName);
                const additionalCategoriesNeedMigration =
                    DB.objectStoreNames.contains(STORES.additionalCategories.objectStoreName);
                const contactsNeedMigration = DB.objectStoreNames.contains(STORES.contacts.objectStoreName);
                const communicationChannelsNeedMigration =
                    DB.objectStoreNames.contains(STORES.communicationChannels.objectStoreName);

                if (optionListsNeedMigration) {
                    DB.deleteObjectStore(STORES.optionLists.objectStoreName);
                }
                if (translationsNeedMigration) {
                    DB.deleteObjectStore(STORES.translations.objectStoreName);
                }
                if (additionalCategoriesNeedMigration) {
                    DB.deleteObjectStore(STORES.additionalCategories.objectStoreName);
                }
                if (contactsNeedMigration) {
                    DB.deleteObjectStore(STORES.contacts.objectStoreName);
                }
                if (communicationChannelsNeedMigration) {
                    DB.deleteObjectStore(STORES.communicationChannels.objectStoreName);
                }

                const optionListsObjectStore = DB.createObjectStore(STORES.optionLists.objectStoreName, {
                    keyPath: STORES.optionLists.keys
                });
                optionListsObjectStore.createIndex(
                    STORES.optionLists.indexes.optionListName,
                    STORES.optionLists.indexes.optionListName,
                    { unique: false }
                );

                const translationsObjectStore = DB.createObjectStore(STORES.translations.objectStoreName, {
                    keyPath: STORES.translations.keys
                });
                translationsObjectStore.createIndex(
                    STORES.translations.indexes.languageKey,
                    STORES.translations.indexes.languageKey,
                    { unique: false }
                );

                const additionalCategoriesObjectStore = DB.createObjectStore(STORES.additionalCategories.objectStoreName, {
                    keyPath: STORES.additionalCategories.keys
                });
                additionalCategoriesObjectStore.createIndex(
                    STORES.additionalCategories.indexes.additionalCategoryNameOrCode,
                    STORES.additionalCategories.indexes.additionalCategoryNameOrCode,
                    { unique: false }
                );

                const contactsObjectStore = DB.createObjectStore(STORES.contacts.objectStoreName, {
                    keyPath: STORES.contacts.keys
                });
                contactsObjectStore.createIndex(
                    STORES.contacts.indexes.userId,
                    STORES.contacts.indexes.userId,
                    { unique: false }
                );

                const communicationChannelsObjectStore = DB.createObjectStore(STORES.communicationChannels.objectStoreName, {
                    keyPath: STORES.communicationChannels.keys
                });
                communicationChannelsObjectStore.createIndex(
                    STORES.communicationChannels.indexes.userId,
                    STORES.communicationChannels.indexes.userId,
                    { unique: false }
                );

                logger.debug(`IndexedDBProvider: Successfully initialized IndexedDB ${DATABASE_NAME} with version ${SCHEMA_VERSION}`);
            };

        }

    }, []);

    if (loading) {
        return <Loader />;
    }

    return children;
};

export { IndexedDBProvider, useIndexedDB };
